/** @format */

import React, { useState, useRef } from "react";
import {
  AddIcon,
  AttachmentIcon,
  ImageIcon,
  FileIcon,
  SearchIcon
} from "../../components/common/IconComponents";

const AttachmentsSection = () => {
  const [files, setFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const fileType = file.type === "image/jpeg" ? "image" : "file";
    const fileSize = file.size / 1024 / 1024; // File size in MB

    // File size limit validation (e.g., 10MB)
    if (fileSize > 10) {
      alert("File size exceeds the limit. Please choose a smaller file.");
      return;
    }

    setIsLoading(true);

    // Simulating file upload delay with setTimeout
    setTimeout(() => {
      const newFiles = [...files, { file, type: fileType }];
      setFiles(newFiles);
      setIsLoading(false);
    }, 2000);
  };

  const iconStyle = {
    backgroundColor: "#F5F5FF",
    border: "1px solid #DFE0F3",
    padding: "8px",
  };

  const toggleModal = () => {
    setIsModalOpen((prevState) => !prevState);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDelete = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  return (
    <div className="p-3 w-96">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center text-[#616161]">
          <AttachmentIcon className="mr-2" />
          <h2 className="text-lg font-thin">Attachments</h2>
        </div>
        <button className="text-blue-500 text-sm" onClick={toggleModal}>
          View All
        </button>
      </div>

      {/* Add New File */}
      <StyledFileInput
        handleFileUpload={handleFileUpload}
        iconStyle={iconStyle}
      />

      {/* List of Submitted Files */}
      <div>
        {files.slice(0, 3).map((file, index) => (
          <AttachmentItem
            key={index}
            file={file}
            onDelete={() => handleDelete(index)}
          />
        ))}
      </div>

      {/* View All Modal */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onRequestClose={toggleModal}>
          <h2 className="text-lg font-medium mb-3 ">All Attachments</h2>
          <div className="mb-2 relative flex items-center">
            <SearchIcon
              className="w-3 h-5 absolute ml-3 pointer-events-none"
              icon={SearchIcon}
            />
            <input
              type="text"
              className="  px-2 py-1  pr-3 pl-10 border-0 bg-gray-50 w-96 rounded-3xl placeholder-p-2"
              placeholder="search"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

    

          <div>
            {files
              .filter((file) =>
                file.file.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((file, index) => (
                <AttachmentItem
                  key={index}
                  file={file}
                  onDelete={() => handleDelete(index)}
                />
              ))}
          </div>
          
        </Modal>
      )}

      {/* Loading Indicator */}
      {isLoading && <LoadingIndicator />}
    </div>
  );
};

const StyledFileInput = ({ handleFileUpload, iconStyle }) => {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="flex items-center mb-2 ">
      <div className="flex items-center">
        <AddIcon
          className="mr-2"
          style={iconStyle}
          onClick={handleClick}
          role="button"
        />
        <div>
          <p className="font-thin">Add a New File</p>
          <p className="text-sm text-gray-500">10 MB max</p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.pdf"
        className="hidden"
        onChange={handleFileUpload}
      />
    </div>
  );
};

const AttachmentItem = ({ file, onDelete }) => {
  const iconStyle = {
    backgroundColor: "#F5F5FF",
    border: "1px solid #DFE0F3",
    padding: "8px",
  };

  const { type, file: uploadedFile } = file;
const currentDate = new Date();
const options = { month: "short", year: "numeric" };
const formattedDate = currentDate.toLocaleDateString("en-US", options);

  return (
    <div className="flex items-center mb-2">
      {type === "image" ? (
        <ImageIcon className="mr-2" style={iconStyle} />
      ) : (
        <FileIcon className="mr-2" style={iconStyle} />
      )}
      <div>
        <p className="font-thin">{uploadedFile.name}</p>
        <p className="text-sm text-gray-500">
          Shared by applicant on {formattedDate}
        </p>
      </div>
      <button
        className="ml-auto text-gray-600 hover:text-gray-800"
        onClick={onDelete}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4"
        >
          <path
            fillRule="evenodd"
            d="M12.707 10l4.647-4.646a.5.5 0 0 0-.708-.708L12 9.293 7.354 4.646a.5.5 0 0 0-.708.708L11.293 10l-4.647 4.646a.5.5 0 0 0 .708.708L12 10.707l4.647 4.647a.5.5 0 0 0 .708-.708L12.707 10z"
          />
        </svg>
      </button>
    </div>
  );
};

const Modal = ({ isOpen, onRequestClose, children }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-opacity-50 bg-[#F5F5FFCC]">
      <div className="bg-white w-96 rounded-lg shadow-lg p-3">
        <div className="flex justify-end">
          <button
            className="text-gray-600 hover:text-gray-800"
            onClick={onRequestClose}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-4 h-4"
            >
              <path
                fillRule="evenodd"
                d="M12.707 10l4.647-4.646a.5.5 0 0 0-.708-.708L12 9.293 7.354 4.646a.5.5 0 0 0-.708.708L11.293 10l-4.647 4.646a.5.5 0 0 0 .708.708L12 10.707l4.647 4.647a.5.5 0 0 0 .708-.708L12.707 10z"
              />
            </svg>
          </button>
        </div>
        {children}
        
      </div>
    </div>
  );
};

const LoadingIndicator = () => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-opacity-50 bg-gray-900 text-white">
      <div className="flex items-center">
        <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin"></div>
        <p className="ml-3">Loading...</p>
      </div>
    </div>
  );
};

export default AttachmentsSection;
